import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoginScreen from './components/LoginScreen';
import HomeScreen from './components/HomeScreen';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/AdminDashboard';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import MasterDataManagement from './pages/MasterDataManagement';
import AuditLogs from './pages/AuditLogs';
import AdminProfile from './pages/AdminProfile';
import SessionManagement from './pages/admin/SessionManagement';
import CommitteeManagement from './pages/admin/CommitteeManagement';

import SecretariatLayout from './layouts/SecretariatLayout';
import SecretariatDashboard from './pages/secretariat/SecretariatDashboard';
import QuestionBank from './pages/secretariat/QuestionBank';
import AgendaManager from './pages/secretariat/AgendaManager';
import FinanceManager from './pages/secretariat/FinanceManager';
import CommitteeManager from './pages/secretariat/CommitteeManager';
import DocumentRepo from './pages/secretariat/DocumentRepo';
import ReportsCenter from './pages/secretariat/ReportsCenter';
import Profile from './pages/secretariat/Profile';

import MinisterDashboard from './pages/MinisterDashboard';
import MinisterLayout from './layouts/MinisterLayout';
import MinisterAgendas from './pages/minister/MinisterAgendas';
import MinisterBills from './pages/minister/MinisterBills';
import MinisterQuestions from './pages/minister/MinisterQuestions';
import MinisterReports from './pages/minister/MinisterReports';
import MinisterProfile from './pages/minister/MinisterProfile';
import './App.css';


// Global Session Interceptor Component
const SessionInterceptor = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate('/');
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate]);

  return children;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) return <Navigate to="/" />;

  if (allowedRoles && (!user.role || !allowedRoles.includes(user.role))) {
    if (user.role === 'SuperAdmin') return <Navigate to="/admin/dashboard" />;
    return <Navigate to="/home" />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <SessionInterceptor>
        <div className="App">
          <Routes>
            <Route path="/" element={<LoginScreen />} />

            {/* Regular User Routes */}
            <Route path="/home" element={
              <ProtectedRoute allowedRoles={['MLA', 'MP', 'MLC', 'Staff', 'Minister', 'Speaker', 'Secretary']}>
                <HomeScreen />
              </ProtectedRoute>
            } />

            {/* Role-Specific Dashboards */}
            <Route path="/legislator/dashboard" element={
              <ProtectedRoute allowedRoles={['MLA', 'MLC']}>
                <HomeScreen />
              </ProtectedRoute>
            } />

            {/* Minister Routes */}
            <Route path="/minister" element={
              <ProtectedRoute allowedRoles={['Minister']}>
                <MinisterLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="dashboard" />} />
              <Route path="dashboard" element={<MinisterDashboard />} />
              <Route path="agendas" element={<MinisterAgendas />} />
              <Route path="bills" element={<MinisterBills />} />
              <Route path="questions" element={<MinisterQuestions />} />
              <Route path="reports" element={<MinisterReports />} />
              <Route path="profile" element={<MinisterProfile />} />
              <Route path="*" element={<Navigate to="/minister/dashboard" />} />
            </Route>

            <Route path="/speaker/dashboard" element={
              <ProtectedRoute allowedRoles={['Speaker']}>
                <HomeScreen />
              </ProtectedRoute>
            } />
            <Route path="/staff/dashboard" element={
              <ProtectedRoute allowedRoles={['Staff', 'Secretary']}>
                <HomeScreen />
              </ProtectedRoute>
            } />

            {/* Super Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['SuperAdmin']}>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="dashboard" />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="roles" element={<RoleManagement />} />
              <Route path="master-data" element={<MasterDataManagement />} />
              <Route path="committees" element={<CommitteeManagement />} />
              <Route path="sessions" element={<SessionManagement />} />
              <Route path="audit" element={<AuditLogs />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="*" element={<Navigate to="/admin/dashboard" />} />
            </Route>

            {/* Secretariat Routes */}
            <Route path="/secretariat" element={
              <ProtectedRoute allowedRoles={['Secretary', 'Secretariat', 'Staff']}>
                <SecretariatLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="dashboard" />} />
              <Route path="dashboard" element={<SecretariatDashboard />} />
              <Route path="questions" element={<QuestionBank />} />
              <Route path="agendas" element={<AgendaManager />} />
              <Route path="finance" element={<FinanceManager />} />
              <Route path="meetings" element={<CommitteeManager />} />
              <Route path="reports" element={<ReportsCenter />} />
              <Route path="profile" element={<Profile />} />
              <Route path="*" element={<Navigate to="/secretariat/dashboard" />} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes >
        </div >
      </SessionInterceptor >
    </Router >
  );
}

export default App;
